/**
 * /it/product-discovery — product discovery concept page (Italian).
 * Links /it/trascrizione-audio + /it/registro-delle-decisioni.
 */
import { MarketingShell } from './MarketingShell';

export function ItProductDiscovery() {
  return (
    <MarketingShell
      currentLocale="it"
      hreflangPath="/product-discovery"
      hreflangLocales={['it']}
      documentTitle="Product discovery: cos'è, metodi e come applicarla · Draftlize"
      documentDescription="Il product discovery in pratica: metodi di intervista, sintesi degli insight e come trasformarli in decisioni di prodotto tracciabili."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guida · Discovery</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Product discovery<br />
            <em>— dall'insight alla decisione.</em>
          </h1>
          <p className="lede">
            Il product discovery è l'attività di verificare <em>cosa</em> costruire prima di costruirlo: interviste, osservazione e validazione per trasformare le ipotesi in fatti. Anche quando è fatto bene, il punto debole è sempre lo stesso: gli <em>insight si perdono prima di diventare decisioni</em>. Qui i metodi e come trasformare le scoperte in decisioni tracciabili.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Metodi di discovery.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Interviste utente</strong> — ascolta il problema e il contesto, non le risposte.</li>
            <li><strong>Osservazione e dati di comportamento</strong> — guarda cosa fanno, non solo cosa dicono.</li>
            <li><strong>Validazione (prototipi, esperimenti)</strong> — testa le ipotesi in piccolo.</li>
            <li><strong>Sintesi</strong> — condensa scoperte sparse in pattern sufficienti per decidere.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Parti dalla <a href="/it/trascrizione-audio" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>trascrizione audio</a> per avere la materia prima. Ma la trascrizione non è il traguardo: è il punto di partenza verso la sintesi e la decisione.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">L'anello debole: tra scoperta e decisione. <em>Collegalo.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Il risultato del discovery non sono le frasi delle interviste, ma le <em>decisioni</em> che prendi a partire da esse. Molti team riassumono le scoperte in una presentazione e finisce lì: la decisione e il suo perché non vivono da nessuna parte. Draftlize collega «verbatim → decisione → requisito».
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Perché gli insight si perdono</h3>
            <p>I momenti chiave dell'intervista si disperdono in pagine Notion o verbali, e allo sprint dopo nessuno li rilegge, perché il «perché» della decisione resta scollegato dalla scoperta.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Collega «verbatim → decisione → requisito»</h3>
            <p>In Draftlize colleghi la frase di un utente alla decisione che ha innescato e al requisito che ne deriva. Se una validazione successiva ribalta l'ipotesi, ciò che dipende diventa «stale».</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Il tuo agente IA parte dalle scoperte</h3>
            <p>Le decisioni del discovery diventano contesto che Claude Code o Cursor leggono via MCP. La prossima spec si scrive sapendo «l'utente ha detto questo, perciò abbiamo deciso quello». Prosegui con <a href="/it/registro-delle-decisioni" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>registro delle decisioni</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Il valore del discovery non sta in quanti insight raccogli, ma nel fatto che sei mesi dopo puoi ancora rintracciare le decisioni prese con essi.
        </blockquote>
        <cite>Collega la scoperta alla decisione e segnala quando l'ipotesi cambia. Questo è Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Domande frequenti.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Discovery o delivery?', a: 'Il discovery verifica «se costruire la cosa giusta»; il delivery è «costruirla bene». Vanno insieme, ma se le decisioni del discovery non restano, il delivery sbaglia direzione.' },
            { q: 'Quante interviste?', a: 'Più di un numero esatto, finché non emergono nuove scoperte (saturazione). Conta non la quantità, ma a quale decisione hai collegato ogni frase.' },
            { q: 'Come sintetizzo le scoperte?', a: 'Raggruppa le frasi per tema e cerca pattern sufficienti per decidere. In Draftlize ogni pattern diventa una scheda di decisione collegata alle frasi originali.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Collega la frase di un'intervista alla decisione <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Registrati e ricevi $5</span>
          <span>Paghi solo ciò che usi</span>
          <span>Il saldo non scade mai</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Trasforma le scoperte del discovery in schede «verbatim → decisione → requisito» in Draftlize. Se l'ipotesi cambia, si segna «stale» da sola, e il tuo agente IA la legge.
          </p>
          <a className="btn-primary" href="/">Inizia gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
